/** @jsx jsx */
import { Card, Flex, Link, Paragraph } from "@eworkit/iris";
import React, { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { LazyUserAvatar } from "@/components-new";
import { LazyCompanyAvatar } from "@/components/avatars/LazyCompanyAvatar";
import { Comment } from "@/components/comments/Comment";
import { ICompany } from "@/data/companies";
import { IConsultantProfile } from "@/data/consultant-profiles";
import { hasPendingRequestToJoin } from "@/data/consultant-profiles/utils/hasPendingRequestToJoin";
import { isActive } from "@/data/consultant-profiles/utils/isActive";
import { isBlocked } from "@/data/consultant-profiles/utils/isBlocked";
import { formatFullName } from "@/data/persons";
import { useFormats } from "@/hooks/Formats";

import { ConsultantProfileActiveStatus } from "./ConsultantProfileActiveStatus";

interface ConsultantProfileCardProps {
  profile: IConsultantProfile;
  onClick?: () => void;
}

export const ConsultantProfileCard: FC<ConsultantProfileCardProps> = ({ profile, onClick }) => {
  const { t } = useTranslation("session");

  const { formatDateTime } = useFormats();

  const isProfileActive = isActive(profile);
  const isProfileBlocked = isBlocked(profile);
  const hasProfilePendingRequestToJoin = hasPendingRequestToJoin(profile);

  const companyProfile =
    (profile.sharedProfile && profile.sharedProfile.company) ||
    (profile.requestToJoin && profile.requestToJoin.company);

  const profileRole = companyProfile
    ? t(`COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.${profile.companyRole}`)
    : t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.PRIVATE_PROFILE");

  return (
    <Card
      sx={{
        borderRadius: [0, null, "lg"],
        borderLeftWidth: [0, null, "1px"],
        borderRightWidth: [0, null, "1px"],
        px: ["sm", "sm", "md"],
        py: isProfileActive ? ["sm", "sm", "sm", "sm"] : ["md", "md", "md", "md"],

        "&:not(:last-child)": {
          borderBottomWidth: [0, null, "1px"],
        },
      }}
      onClick={onClick}>
      <Flex flexDirection="column" gap="sm">
        <Flex justifyContent="space-between" alignItems="center" gap="sm">
          <Flex gap="xs">
            <div sx={{ opacity: isProfileActive ? 1 : 0.5 }}>
              {hasProfilePendingRequestToJoin ? (
                <LazyCompanyAvatar company={companyProfile!} />
              ) : (
                <LazyUserAvatar
                  avatar={profile.avatar}
                  firstName={profile.firstName}
                  lastName={profile.lastName}
                  company={companyProfile as ICompany}
                />
              )}
            </div>
            <Flex flexDirection="column">
              <Paragraph bold color={isProfileActive ? "n1000" : "n700"}>
                {hasProfilePendingRequestToJoin ? companyProfile!.name : formatFullName(profile)}
              </Paragraph>
              <Paragraph color={isProfileActive ? "n1000" : "n700"}>
                {hasProfilePendingRequestToJoin
                  ? `${companyProfile?.countryCode} ${companyProfile?.vatNo}`
                  : companyProfile
                  ? t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.ROLE_AT_COMPANY", {
                      role: profileRole,
                      company: companyProfile?.name,
                    })
                  : profileRole}
              </Paragraph>
            </Flex>
          </Flex>

          <ConsultantProfileActiveStatus profile={profile} />
        </Flex>

        {isProfileBlocked && (
          <React.Fragment>
            <Paragraph>{t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.YOUR_COMPANY_IS_BLOCKED")}</Paragraph>

            {profile.reasonOfBlock && (
              <Comment>
                <Flex gap="xs">
                  <Paragraph bold>{t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.COMMENT")}</Paragraph>
                  {companyProfile?.modifiedDate && (
                    <Paragraph color="n700">{formatDateTime(companyProfile.modifiedDate)}</Paragraph>
                  )}
                </Flex>
                <Paragraph>{profile.reasonOfBlock}</Paragraph>
              </Comment>
            )}

            <Paragraph>
              <Trans ns="session" i18nKey="COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.CONTACT_US_FOR_MORE_INFO">
                <Link as="a" href="mailto:support@verama.com" variant="primary" />
              </Trans>
            </Paragraph>
          </React.Fragment>
        )}

        {hasProfilePendingRequestToJoin && (
          <React.Fragment>
            <Paragraph>
              {t("COMPONENTS.TOPBAR.CONTEXT_DROPDOWN.YOU_ASKED_TO_JOIN", { date: formatDateTime(profile.createdDate) })}
            </Paragraph>
          </React.Fragment>
        )}
      </Flex>
    </Card>
  );
};
